import { useState, useEffect, useMemo } from "react";
import { useQuery } from '@tanstack/react-query';

import axios from "axios";
import config from '../../config';

import '../../styles/global.css';
import '../../styles/header.css';
import '../../styles/tables.css';
import '../../styles/buttons.css';
import '../../styles/popup.css';
import '../../styles/status.css';

import PageHeader from "../../components/general/pageHeader";
import MoneyCard from "../../components/cards/moneyCard";
import BalanceTransactionCards from "../../components/cards/rBalanceTransactionCards";
import LoadBalanceForm from "../../components/forms/loadBalanceForm";
import Loading from "../../components/general/loading";
import useAuthenticate from "../../components/authentication/authenticate";

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

const RBalanceTransactions = () => {
    const { user } = useAuthenticate();

    const accountName = `${user?.user?.userData?.givenName?.replace(/^./, c => c.toUpperCase()) || ""} ${user?.user?.userData?.lastName?.replace(/^./, c => c.toUpperCase()) || ""}`.trim();

    const accountId = useMemo(() => {
        if (user?.user?.active?.tenant) {
            return { type: "tenant", value: user.user.active.tenant };
        } else if (user?.user?.active?.owner) {
            return { type: "owner", value: user.user.active.owner };
        }
        return null;
    }, [user?.user?.active?.tenant, user?.user?.active?.owner]);

    const { data: properties, isLoading: isPropertiesLoading } = useQuery({
        queryKey: ['v2-properties'],
        queryFn: fetchProperties,
    });

    const { data: rolesData, isLoading: isRolesLoading } = useQuery({
        queryKey: ['v2-roles'],
        queryFn: fetchRoles,
    });

    const { data: creditBalances, isLoading: isCreditBalancesLoading } = useQuery({
        queryKey: ['v2-creditBalances'],
        queryFn: fetchBalances,
    });

    const { data: transactions, isLoading: isTransactionsLoading } = useQuery({
        queryKey: ['v2-transactions'],
        queryFn: fetchTransactions,
    });

    const { data: invoices, isLoading: isInvoicesLoading } = useQuery({
        queryKey: ['v2-invoices'],
        queryFn: fetchInvoices,
    });

    const [selectedAccount, setSelectedAccount] = useState(null);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [selectedUnits, setSelectedUnits] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);

    const [invoiceOverlayOpen, setInvoiceOverlayOpen] = useState(false);
    const [paymentOverlayOpen, setPaymentOverlayOpen] = useState(false);

    const [activeTab, setActiveTab] = useState('invoices');

    // Set account to active account
    useEffect(() => {
        if (accountId) {
            setSelectedAccount({
                ...accountId,
                label: accountName || "My Account",
            });
        }
    }, [accountId, accountName]);

    // Filter properties owned by the selected account
    const propertyOptions = useMemo(() => {
        if (!selectedAccount) return [];
        const relevant = rolesData?.filter(
            r => r.propertyRole.account._id === selectedAccount.value
        );
        const props = new Set();
        relevant?.forEach(r => props.add(r.propertyRole.property));
        return properties?.filter(p => props.has(p.value));
    }, [rolesData, selectedAccount, properties]);

    // Filter units owned by the selected account and selected property
    const unitOptions = useMemo(() => {
        if (!selectedProperty || !selectedAccount) return [];
        const relevant = rolesData?.filter(
            r =>
                r.propertyRole.account._id === selectedAccount.value &&
                r.propertyRole.property === selectedProperty.value
        );
        const map = {};
        relevant?.forEach(r => {
            map[r.unit.unitNumber] = {
                label: r.unit.unitNumber,
                value: r.unit.unitNumber,
            };
        });
        return Object.values(map);
    }, [rolesData, selectedAccount, selectedProperty]);

    // Set to first property once selected account is set
    useEffect(() => {
        if (!isPropertiesLoading && selectedAccount && !selectedProperty) {
            setSelectedProperty(propertyOptions?.[0] || null);
        }
    }, [isPropertiesLoading, propertyOptions, selectedAccount, selectedProperty]);

    // Combine payment transactions and invoices
    const { combinedRows, accountInvoices } = useMemo(() => {
        if (!selectedAccount) return { combinedRows: [], accountInvoices: [] };

        // Filter only data related to the selected account
        const accountTransactions = transactions?.filter(
            txn => txn.account?._id === selectedAccount.value
        ) || [];

        const accountInvoices = invoices?.filter(
            inv => inv.chargedTo?.propertyRole?.account === selectedAccount.value
        ) || [];

        // Format transactions (payments)
        const formattedTransactions = accountTransactions.map(txn => {
            const paymentDetails = txn.transactionRecord?.payment_details;

            return {
                id: txn._id,
                type: "Payment",
                property: txn.accountBalance?.propertyRole?.property?.name || null,
                reference: txn.transactionReference || "-",
                cd: txn.type === "credit" ? "Credit" : "Debit",
                issuedBy: null,
                vendor: null,
                isRecurring: null,
                status: null,
                createdAt: txn.createdAt,
                updatedAt: txn.updatedAt,
                dueDate: null,
                amount: txn.amount,
                invoiceItems: [],
                paymentDate: txn.createdAt,
                paymentMethod: paymentDetails?.type
                    ? paymentDetails.type.charAt(0).toUpperCase() + paymentDetails.type.slice(1)
                    : null,
                cardNumber: paymentDetails?.last4 || null,
                allocatedAmount: null,
                openBalance: null,
                isInvoice: false,
            };
        });

        // Format invoices
        const formattedInvoices = accountInvoices.map(inv => {
            const allocatedAmount = inv.totalAmount - inv.balance;

            return {
                id: inv._id,
                type: "Invoice",
                property: inv.property.name || null,
                unit: inv.invoiceItems?.[0]?.unit?.unitNumber || null,
                reference: inv.status?.toLowerCase() === "pending" ? "-" : inv._id,
                cd: "Debit",
                issuedBy: inv.issuedBy?.user?.givenName || null,
                vendor: inv.vendor || null,
                isRecurring: inv.frequency || null,
                status: inv.status || null,
                createdAt: inv.createdAt,
                updatedAt: inv.updatedAt,
                dueDate: inv.dueDate,
                amount: inv.totalAmount,
                invoiceItems: inv.invoiceItems || [],
                paymentDate: inv.updatedAt,
                paymentMethod: null,
                cardNumber: null,
                allocatedAmount,
                openBalance: inv.balance,
                isInvoice: true,
            };
        });

        const combinedRows = [...formattedTransactions, ...formattedInvoices].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        return { combinedRows, accountInvoices };
    }, [transactions, invoices, selectedAccount]);

    // Calculating the balance, invoice amount, and past due amount
    const balanceEntry = creditBalances?.find(b => b.propertyRole?.account === selectedAccount?.value);
    const now = new Date();
    const invoiceAmount = accountInvoices
        ?.filter(t => t.status !== "Paid")
        ?.reduce((sum, t) => sum + (t.balance ?? 0), 0);
    const pastDue = accountInvoices
        ?.filter(t => t.status !== "Paid" && new Date(t.dueDate) < now)
        ?.reduce((sum, t) => sum + (t.balance ?? 0), 0);
    const balance = balanceEntry?.credit || 0;

    // Filtering combinedRows based on property and unit selection
    const filteredRows = useMemo(() => {
        if (!selectedProperty) {
            return null; // means "no property selected"
        }

        // Start from combined rows
        let rows = [...combinedRows];

        // Filter by property
        rows = rows.filter(r => r.property === selectedProperty.label);

        // Filter invoices by unit if selected
        if (selectedUnits?.length > 0) {
            const selectedUnitNumbers = selectedUnits.map(u => u.value);
            rows = rows.filter(r =>
                r.isInvoice ? selectedUnitNumbers.includes(r.unit) : true
            );
        }

        return rows;
    }, [combinedRows, selectedProperty, selectedUnits]);

    // View invoice or payment details
    const handleRowClick = (item) => {
        console.log("Selected item:", item);
        setSelectedRow(item);
        if (item.isInvoice) {
            setInvoiceOverlayOpen(true);
        }
        else {
            setPaymentOverlayOpen(true);
        }
    };

    const isAnyLoading =
        isRolesLoading ||
        isPropertiesLoading ||
        isCreditBalancesLoading ||
        isTransactionsLoading ||
        isInvoicesLoading;

    if (isAnyLoading) return <Loading />;

    return (
        <div className="w-full">
            <PageHeader pageName="Balance and Transactions" />
            <div className="flex flex-col md:flex-row md:gap-[15px] space-between mt-[20px] pb-[15px] border-b-[1.5px] border-[#E8E8E8] gap-0">
                <div className="account-dropdown md:mr-[15px]">
                    <div className="mb-[15px]">
                        <p className="text-xl font-poppins">{selectedAccount?.label} </p>
                    </div>
                    <div className="w-full md:w-[300px] mb-[10px]">
                        <Autocomplete
                            options={propertyOptions}
                            value={selectedProperty}
                            onChange={(e, val) => {
                                setSelectedProperty(val);
                                setSelectedUnits([]);
                            }}
                            renderInput={(params) => (
                                <TextField {...params} label="Select Property" variant="outlined" fullWidth />
                            )}
                        />
                    </div>
                    <div className="w-full md:w-[300px] mb-[20px] md:mb-0">
                        <Autocomplete
                            options={unitOptions}
                            value={selectedUnits}
                            onChange={(e, val) => setSelectedUnits(val)}
                            multiple
                            renderInput={(params) => (
                                <TextField {...params} label="Select Unit" variant="outlined" fullWidth />
                            )}
                        />
                    </div>
                </div>
                <div className="flex flex-col md:flex-row gap-[15px] w-full">
                    <MoneyCard
                        label="Remaining Balance"
                        info={
                            <>
                                PHP<br />
                                {formatMoney(balance)}
                            </>
                        }
                    />
                    <MoneyCard
                        label="Invoice Amount"
                        info={
                            <>
                                PHP<br />
                                {formatMoney(invoiceAmount)}
                            </>
                        }
                    />
                    <MoneyCard
                        label="Invoice Past Due"
                        info={
                            <>
                                PHP<br />
                                {formatMoney(pastDue)}
                            </>
                        }
                    />
                </div>
            </div>
            <div className="flex flex-col md:flex-row w-full justify-between mt-[20px] mb-[20px]">
                <div className="flex space-between gap-[10px] mb-[15px] md:mb-0">
                    <button
                        className={`tab-buttons ${activeTab === 'invoices' ? 'active' : ''}`}
                        onClick={() => setActiveTab('invoices')}
                        style={{ width: '100%', height: '60px' }}
                    >
                        Invoices
                    </button>
                    <button
                        className={`tab-buttons ${activeTab === 'payments' ? 'active' : ''}`}
                        onClick={() => setActiveTab('payments')}
                        style={{ width: '100%', height: '60px' }}
                    >
                        Payments
                    </button>
                </div>
                <div className="bt-button w-full h-[80px] md:flex-row md:w-[130px] md:h-[60px]">
                    <LoadBalanceForm selectedAccount={selectedAccount} currentBalance={balance} />
                </div>
            </div>


            {/* Desktop View */}
            <div className="page-table-container hidden md:block">
                <table className="page-table">
                    <thead>
                        <tr>
                            <th>Reference No.</th>
                            <th>Date Created</th>
                            <th>Date Due</th>
                            <th>Amount</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredRows === null ? (
                            <tr><td colSpan="5" style={{ textAlign: 'center' }}>Please select a property</td></tr>
                        ) : filteredRows
                            ?.filter(row => (activeTab === 'invoices' ? row.isInvoice : !row.isInvoice))
                            ?.length > 0 ? (
                            filteredRows
                                .filter(row => (activeTab === 'invoices' ? row.isInvoice : !row.isInvoice))
                                .map(row => (
                                    <tr
                                        key={row.id}
                                        className={selectedRow?.id === row.id ? 'selected' : ''}
                                        onClick={() => handleRowClick(row)}
                                    >
                                        <td>{row.reference}</td>
                                        <td>{row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '-'}</td>
                                        <td>
                                            {row.dueDate && !isNaN(new Date(row.dueDate).getTime())
                                                ? new Date(row.dueDate).toLocaleDateString()
                                                : '-'}
                                        </td>
                                        <td>PHP {formatMoney(row.amount)}</td>
                                        <td>{row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : '-'}</td>
                                    </tr>
                                ))
                        ) : (
                            <tr><td colSpan="5" style={{ textAlign: 'center' }}>No data found</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Mobile View */}
            <BalanceTransactionCards
                combinedRows={combinedRows}
                activeTab={activeTab}
                selectedRow={selectedRow}
                handleRowClick={handleRowClick}
            />

            {invoiceOverlayOpen && (
                <div className="popup-overlay">
                    <div className="popup-content">
                        <button className="close-popup" onClick={() => {
                            setInvoiceOverlayOpen(false);
                            setSelectedRow(null);
                        }}>
                            <i className="fas fa-times"></i>
                        </button>
                        <h2 className="text-2xl">Invoice Details</h2>
                        <table className="form-table">
                            <tbody>
                                <tr>
                                    <td style={{ width: "300px" }}>
                                        <div className="detail-column"
                                        >
                                            <p className="detail-label">Property</p>
                                            <p className="detail">x</p>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="detail-column">
                                            <p className="detail-label">Unit</p>
                                            <p className="detail">x</p>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="w-[180px] md:w-[300px]">
                                        <div className="detail-column">
                                            <p className="detail-label">Charged To</p>
                                            <p className="detail">{accountName}</p>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="detail-column">
                                            <p className="detail-label">Issued By</p>
                                            <p className="detail">{selectedRow.issuedBy ?? '-'}</p>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="w-[180px] md:w-[300px]">
                                        <div className="detail-column"
                                        >
                                            <p className="detail-label">Vendor</p>
                                            <p className="detail">{selectedRow.vendor ?? '-'}</p>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="detail-column">
                                            <p className="detail-label">Is Recurring</p>
                                            <p className="detail">{selectedRow.isRecurring ?? '-'}</p>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="w-[180px] md:w-[300px]">
                                        <div className="detail-column"
                                        >
                                            <p className="detail-label">Due Date</p>
                                            <p className="detail">{selectedRow.dueDate ? new Date(selectedRow.dueDate).toLocaleDateString() : '-'}</p>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="detail-column">
                                            <p className="detail-label">Status</p>
                                            <p className="detail">
                                                {(selectedRow.status ?? '-').charAt(0).toUpperCase() + (selectedRow.status ?? '-').slice(1)}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <h3 style={{ marginBottom: '5px' }}>Invoice Items</h3>
                        <p className="paid-amount mb-4"><span>Total Amount:</span> PHP {selectedRow.amount}</p>
                        <table className="invoice-items-table">
                            <thead>
                                <tr>
                                    <th>Invoice Category</th>
                                    <th>Description</th>
                                    <th>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedRow.invoiceItems?.map((item) => (
                                    <tr key={item._id}>
                                        <td>{item.itemName?.trim() ? item.itemName : '-'}</td>
                                        <td>{item.description}</td>
                                        <td>PHP {formatMoney(item.amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {paymentOverlayOpen && (
                <div className="popup-overlay">
                    <div className="popup-content">
                        <button className="close-popup" onClick={() => {
                            setPaymentOverlayOpen(false);
                            setSelectedRow(null);
                        }}>
                            <i className="fas fa-times"></i>
                        </button>
                        <h2 className="text-2xl mb-[30px]">Payment Details</h2>
                        <div className="detail-column">
                            <p className="detail-label">Reference Number</p>
                            <p className="detail">{selectedRow.reference}</p>
                        </div>
                        <table className="form-table">
                            <tbody>
                                <tr>
                                    <td className="w-[180px] md:w-[300px]">
                                        <div className="detail-column">
                                            <p className="detail-label">Account</p>
                                            <p className="detail">{accountName}</p>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="detail-column">
                                            <p className="detail-label">Payment Date</p>
                                            <p className="detail">{selectedRow.paymentDate ? new Date(selectedRow.paymentDate).toLocaleDateString() : '-'}</p>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <h3 style={{ marginTop: "10px" }}>Payment</h3>
                        <table className="form-table">
                            <tbody>
                                <tr>
                                    <td className="w-[180px] md:w-[300px]">
                                        <div className="detail-column">
                                            <p className="detail-label">Amount</p>
                                            <p className="detail">PHP {formatMoney(selectedRow.amount)}</p>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="detail-column">
                                            <p className="detail-label">Payment Method</p>
                                            <p className="detail">{selectedRow.paymentMethod}</p>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <div className="detail-column">
                                            <p className="detail-label">Card Number</p>
                                            <p className="detail">
                                                {(selectedRow.paymentMethod === 'Card' || selectedRow.paymentMethod === 'Credit Card') && selectedRow.cardNumber
                                                    ? `**** **** **** ${selectedRow.cardNumber}`
                                                    : '-'}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

const fetchProperties = async () => {
    const response = await axios.get(`${config.API_URL}/v2/property`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
    return response.data.map(p => ({ value: p._id, label: p.name }));
};

const fetchRoles = async () => {
    const response = await axios.get(`${config.API_URL}/v2/role/unitRole`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
    return response.data.filter(r => r.propertyRole.role === 'owner' || r.propertyRole.role === 'tenant');
};

const fetchBalances = async () => {
    const response = await axios.get(`${config.API_URL}/v2/accountBalance/balance`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
    return response.data.balances;
};

const fetchTransactions = async () => {
    const response = await axios.get(`${config.API_URL}/v2/accountTransaction/credit`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
    return response.data.transactions;
};

const fetchInvoices = async () => {
    const response = await axios.get(`${config.API_URL}/v2/invoice`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
    return response.data;
};

function formatMoney(amount) {
    const num = Number(amount) || 0;
    return num.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

export default RBalanceTransactions;